import { useEffect, useMemo, useState } from 'react'
import { apiFetch, apiUpload } from '../lib/api'

const POST_TYPES = ['EXPERIENCE', 'LOOKING_FOR_COEATER', 'REVIEW']
const CUISINES = ['Indian', 'Sushi', 'Italian', 'Chinese', 'Dessert']
const COMPOSER_TYPES = [
	{ id: 'story', label: 'Story' },
	{ id: 'post', label: 'Post' },
	{ id: 'reel', label: 'Reel' },
]

function guessMediaType(url) {
	if (!url) return 'TEXT'
	return /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(url) ? 'VIDEO' : 'IMAGE'
}

export default function CreatePost({ open, onClose, onCreated, city, initialKind = 'post' }) {
	const [kind, setKind] = useState(initialKind)
	const [content, setContent] = useState('')
	const [mediaUrl, setMediaUrl] = useState('')
	const [mediaType, setMediaType] = useState('TEXT')
	const [postType, setPostType] = useState('EXPERIENCE')
	const [cuisineTag, setCuisineTag] = useState('')
	const [isLooking, setIsLooking] = useState(false)
	const [mealDateTime, setMealDateTime] = useState('')
	const [seats, setSeats] = useState('')
	const [restaurants, setRestaurants] = useState([])
	const [restaurantQuery, setRestaurantQuery] = useState('')
	const [restaurantId, setRestaurantId] = useState('')
	const [tagQuery, setTagQuery] = useState('')
	const [tagResults, setTagResults] = useState([])
	const [selectedTaggedUsers, setSelectedTaggedUsers] = useState([])
	const [tagLoading, setTagLoading] = useState(false)
	const [loading, setLoading] = useState(false)
	const [uploading, setUploading] = useState(false)
	const [uploadedFileName, setUploadedFileName] = useState('')
	const [error, setError] = useState('')

	const isStory = kind === 'story'
	const isReel = kind === 'reel'
	const mediaAccept = isReel ? 'video/*' : (isStory ? 'image/*,video/*' : 'image/*')
	const submitLabel = isStory ? 'Share Story' : isReel ? 'Publish Reel' : 'Share Post'
	const title = isStory ? 'New Story' : isReel ? 'New Reel' : 'New Post'
	const textareaPlaceholder = isStory
		? 'Add a caption for your story...'
		: isReel
			? 'Add a caption for your reel...'
			: 'Share your dining experience...'

	function resetForm(nextKind = initialKind) {
		setKind(nextKind)
		setContent('')
		setMediaUrl('')
		setMediaType('TEXT')
		setPostType('EXPERIENCE')
		setCuisineTag('')
		setIsLooking(false)
		setMealDateTime('')
		setSeats('')
		setRestaurantId('')
		setRestaurantQuery('')
		setTagQuery('')
		setTagResults([])
		setSelectedTaggedUsers([])
		setUploadedFileName('')
		setError('')
	}

	useEffect(() => {
		if (!open) return
		resetForm(initialKind)
		const cityParam = encodeURIComponent((city || '').trim() || 'Bengaluru')
		apiFetch(`/restaurants?city=${cityParam}`)
			.then(async (res) => {
				if (!res.ok) throw new Error('Could not load restaurants')
				setRestaurants(await res.json())
			})
			.catch(() => {})
	}, [open, city, initialKind])

	const filteredRestaurants = useMemo(() => {
		if (!restaurantQuery.trim()) return restaurants.slice(0, 8)
		const q = restaurantQuery.toLowerCase()
		return restaurants.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 8)
	}, [restaurants, restaurantQuery])

	useEffect(() => {
		if (!open || isStory) {
			setTagResults([])
			setTagLoading(false)
			return undefined
		}

		const normalizedQuery = tagQuery.trim()
		if (!normalizedQuery) {
			setTagResults([])
			setTagLoading(false)
			return undefined
		}

		let cancelled = false
		const timeoutId = setTimeout(async () => {
			setTagLoading(true)
			try {
				const res = await apiFetch(`/users?purpose=tagging&q=${encodeURIComponent(normalizedQuery)}&limit=8`)
				if (!res.ok) throw new Error('Failed to search users')
				const data = await res.json()
				if (cancelled) return

				const selectedIds = new Set(selectedTaggedUsers.map((user) => user.id))
				setTagResults((Array.isArray(data.users) ? data.users : []).filter((user) => !selectedIds.has(user.id)))
			} catch {
				if (!cancelled) setTagResults([])
			} finally {
				if (!cancelled) setTagLoading(false)
			}
		}, 250)

		return () => {
			cancelled = true
			clearTimeout(timeoutId)
		}
	}, [open, isStory, selectedTaggedUsers, tagQuery])

	function addTaggedUser(user) {
		if (!user?.id) return
		setSelectedTaggedUsers((prev) => (prev.some((entry) => entry.id === user.id) ? prev : [...prev, user]))
		setTagQuery('')
		setTagResults([])
	}

	function removeTaggedUser(userId) {
		setSelectedTaggedUsers((prev) => prev.filter((user) => user.id !== userId))
	}

	async function submitPost(e) {
		e.preventDefault()
		setError('')

		const normalizedMediaType = mediaUrl ? mediaType : 'TEXT'
		if (isReel && normalizedMediaType !== 'VIDEO') {
			setError('Reels require a video upload.')
			return
		}

		setLoading(true)
		try {
			const createEndpoint = isStory ? '/home/stories' : (isReel ? '/reels' : '/posts')
			const res = await apiFetch(createEndpoint, {
				method: 'POST',
				body: JSON.stringify(
					isStory
						? {
							caption: content.trim() || null,
							mediaUrl: mediaUrl || null,
							mediaType: normalizedMediaType,
						}
						: {
							content,
							mediaUrl: mediaUrl || null,
							mediaType: normalizedMediaType,
							taggedUserIds: selectedTaggedUsers.map((user) => user.id),
							postType,
							cuisineTag: cuisineTag || null,
							isLookingForCoeater: isLooking,
							mealDateTime: mealDateTime || null,
							seats: seats ? Number(seats) : null,
							restaurantId: restaurantId ? Number(restaurantId) : null,
						}
				),
			})
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || `Failed to create ${kind}`)
			onCreated?.(data, kind)
			resetForm(kind)
			onClose?.()
		} catch (err) {
			setError(err.message)
		} finally {
			setLoading(false)
		}
	}

	async function handleImageUpload(e) {
		const file = e.target.files?.[0]
		if (!file) return

		if (isReel && !file.type.startsWith('video/')) {
			setError('Reels require a video file.')
			return
		}

		setError('')
		setUploading(true)
		try {
			const formData = new FormData()
			formData.append('image', file)

			const res = await apiUpload('/posts/upload-image', formData)
			const data = await res.json()
			if (!res.ok) throw new Error(data.message || 'Failed to upload media')

			setMediaUrl(data.url)
			setMediaType(data.mediaType || (file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE'))
			setUploadedFileName(file.name)
		} catch (err) {
			setError(err.message)
		} finally {
			setUploading(false)
		}
	}

	if (!open) return null

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3">
			<form onSubmit={submitPost} className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white p-4 sm:p-5 max-h-[90vh] overflow-y-auto">
				<div className="flex items-center justify-between mb-3">
					<h3 className="title-font text-lg font-bold text-gray-900">{title}</h3>
					<button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
				</div>

				<div className="mb-4 flex flex-wrap gap-2">
					{COMPOSER_TYPES.map((entry) => (
						<button
							key={entry.id}
							type="button"
							onClick={() => resetForm(entry.id)}
							className={`rounded-full border px-3 py-1 text-xs font-semibold ${kind === entry.id ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-700'}`}
						>
							{entry.label}
						</button>
					))}
				</div>

				<textarea
					value={content}
					onChange={(e) => setContent(e.target.value)}
					rows={4}
					className="w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
					placeholder={textareaPlaceholder}
				/>

				<div className="mt-3 rounded-2xl border border-dashed border-gray-300 p-3">
					<p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Upload {isReel ? 'Video' : isStory ? 'Story Media' : 'Image'}</p>
					<div className="flex flex-wrap items-center gap-2">
						<label className="inline-flex cursor-pointer rounded-full bg-gray-900 px-4 py-2 text-xs font-semibold text-white">
							<input type="file" accept={mediaAccept} onChange={handleImageUpload} className="hidden" />
							{uploading ? 'Uploading...' : isReel ? 'Choose Video' : 'Choose Media'}
						</label>
						{uploadedFileName && <span className="text-xs text-gray-600">{uploadedFileName}</span>}
					</div>
					{mediaUrl && mediaType === 'VIDEO' && (
						<video src={mediaUrl} controls playsInline className="mt-3 max-h-72 w-full rounded-xl border border-gray-200 bg-black object-contain" />
					)}
					{mediaUrl && mediaType !== 'VIDEO' && (
						<img src={mediaUrl} alt="Uploaded" className="mt-3 max-h-52 w-full rounded-xl border border-gray-200 object-cover" />
					)}
				</div>

				<input
					value={mediaUrl}
					onChange={(e) => {
						setUploadedFileName('')
						setMediaUrl(e.target.value)
						setMediaType(guessMediaType(e.target.value))
					}}
					className="mt-3 w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
					placeholder={isReel ? 'Optional video URL' : 'Optional media URL'}
				/>

				{!isStory && (
					<>
						<div className="mt-4">
					<p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Post Type</p>
					<div className="flex flex-wrap gap-2">
						{POST_TYPES.map((type) => (
							<button
								key={type}
								type="button"
								onClick={() => {
									setPostType(type)
									if (type === 'LOOKING_FOR_COEATER') setIsLooking(true)
								}}
								className={`rounded-full border px-3 py-1 text-xs font-semibold ${postType === type ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-700'}`}
							>
								{type.replaceAll('_', ' ')}
							</button>
						))}
					</div>
						</div>

						<div className="mt-4">
					<p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Link To Restaurant (Optional)</p>
					<input
						value={restaurantQuery}
						onChange={(e) => setRestaurantQuery(e.target.value)}
						className="w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
						placeholder="Search restaurant"
					/>
					<div className="mt-2 max-h-28 overflow-y-auto rounded-xl border border-gray-200">
						{filteredRestaurants.length === 0 && <p className="px-3 py-2 text-xs text-gray-500">No matching restaurants</p>}
						{filteredRestaurants.map((restaurant) => (
							<button
								key={restaurant.id}
								type="button"
								onClick={() => setRestaurantId(String(restaurant.id))}
								className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${restaurantId === String(restaurant.id) ? 'bg-orange-50 text-orange-700 font-semibold' : 'hover:bg-gray-50'}`}
							>
								{restaurant.name} • {restaurant.area}
							</button>
						))}
					</div>
						</div>

							<div className="mt-4">
								<p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Tag People (Optional)</p>
								<input
									value={tagQuery}
									onChange={(e) => setTagQuery(e.target.value)}
									className="w-full rounded-2xl border border-gray-300 px-3 py-2 text-sm"
									placeholder="Search users to tag"
								/>
								{selectedTaggedUsers.length > 0 && (
									<div className="mt-2 flex flex-wrap gap-2">
										{selectedTaggedUsers.map((user) => (
											<button
												key={user.id}
												type="button"
												onClick={() => removeTaggedUser(user.id)}
												className="rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-800"
											>
												@{user.name} ×
											</button>
										))}
									</div>
								)}
								{(tagLoading || tagResults.length > 0 || tagQuery.trim()) && (
									<div className="mt-2 max-h-32 overflow-y-auto rounded-xl border border-gray-200">
										{tagLoading && <p className="px-3 py-2 text-xs text-gray-500">Searching users...</p>}
										{!tagLoading && tagResults.length === 0 && tagQuery.trim() && <p className="px-3 py-2 text-xs text-gray-500">No users found</p>}
										{tagResults.map((user) => (
											<button
												key={user.id}
												type="button"
												onClick={() => addTaggedUser(user)}
												className="flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50"
											>
												<span className="font-medium text-gray-900">{user.name}</span>
												<span className="text-xs text-gray-500">{user.location?.city || user.location?.area || 'User'}</span>
											</button>
										))}
									</div>
								)}
							</div>

						<div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
					<select value={cuisineTag} onChange={(e) => setCuisineTag(e.target.value)} className="rounded-2xl border border-gray-300 px-3 py-2 text-sm">
						<option value="">Cuisine tag (optional)</option>
						{CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
					</select>
					<input
						type="number"
						value={seats}
						onChange={(e) => setSeats(e.target.value)}
						min={1}
						max={20}
						className="rounded-2xl border border-gray-300 px-3 py-2 text-sm"
						placeholder="Seats (optional)"
					/>
					<input
						type="datetime-local"
						value={mealDateTime}
						onChange={(e) => setMealDateTime(e.target.value)}
						className="rounded-2xl border border-gray-300 px-3 py-2 text-sm"
					/>
					<label className="flex items-center gap-2 rounded-2xl border border-gray-300 px-3 py-2 text-sm text-gray-700">
						<input type="checkbox" checked={isLooking} onChange={(e) => setIsLooking(e.target.checked)} />
						Looking for co-eaters?
					</label>
						</div>
					</>
				)}

				{error && <p className="mt-3 text-sm font-semibold text-red-600">{error}</p>}

				<div className="mt-4 flex justify-end gap-2">
					<button type="button" onClick={onClose} className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">Cancel</button>
					<button type="submit" disabled={loading} className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
						{loading ? 'Posting...' : submitLabel}
					</button>
				</div>
			</form>
		</div>
	)
}
